import { DiagramModel } from '@diagram/model/diagram.model';
import { DrillDownId, DrillDownModel } from '@diagram/model/drilldown.model';
import { EdgeId, EdgeModel } from '@diagram/model/edge.model';
import { NodeId, NodeModel } from '@diagram/model/node.model';
import { ElementById } from '@diagram/store/store.types';
import { getFirestoreInstance } from '../../../shared/services/firebaseConfig';

export const diagramsRef = async (diagramId: string) => {
  const firestore = await getFirestoreInstance();
  const { doc } = await import('firebase/firestore');
  return doc(firestore, 'diagrams', diagramId);
};

const nodesCollectionRef = async (diagramId: string) => {
  const firestore = await getFirestoreInstance();
  const { collection } = await import('firebase/firestore');
  return collection(firestore, `diagrams/${diagramId}/nodes`);
};

const edgesCollectionRef = async (diagramId: string) => {
  const firestore = await getFirestoreInstance();
  const { collection } = await import('firebase/firestore');
  return collection(firestore, `diagrams/${diagramId}/edges`);
};

const drillDownsCollectionRef = async (diagramId: string) => {
  const firestore = await getFirestoreInstance();
  const { collection } = await import('firebase/firestore');
  return collection(firestore, `diagrams/${diagramId}/drillDowns`);
};

export const loadDiagramFromFirestore = async (
  diagramId: string,
): Promise<{
  nodes: ElementById<NodeModel>;
  edges: ElementById<EdgeModel>;
  drilldowns: ElementById<DrillDownModel>;
  diagram: DiagramModel;
}> => {
  try {
    const { getDoc, getDocs } = await import('firebase/firestore');

    const diagramRef = await diagramsRef(diagramId);
    const nodesRef = await nodesCollectionRef(diagramId);
    const edgesRef = await edgesCollectionRef(diagramId);
    const drillDownsRef = await drillDownsCollectionRef(diagramId);

    const docSnap = await getDoc(diagramRef);
    const nodesSnap = await getDocs(nodesRef);
    const edgesSnap = await getDocs(edgesRef);
    const drillDownsSnap = await getDocs(drillDownsRef);

    const nodes = nodesSnap.docs.reduce((acc, doc) => {
      const data = doc.data();
      acc[doc.id as NodeId] = { ...data, id: doc.id } as NodeModel;
      return acc;
    }, {} as ElementById<NodeModel>);

    const edges = edgesSnap.docs.reduce((acc, doc) => {
      acc[doc.id as EdgeId] = { id: doc.id, ...doc.data() } as EdgeModel;
      return acc;
    }, {} as ElementById<EdgeModel>);

    const drilldowns = drillDownsSnap.docs.reduce((acc, doc) => {
      acc[doc.id as DrillDownId] = {
        ...doc.data(),
        id: doc.id,
      } as DrillDownModel;
      return acc;
    }, {} as ElementById<DrillDownModel>);

    const settings = {
      nodeDistance: docSnap.data()?.settings?.nodeDistance || 150,
      snapToGrid: docSnap.data()?.settings?.snapToGrid || false,
      theme: docSnap.data()?.settings?.theme || 'light',
      gridSize: docSnap.data()?.settings?.gridSize || 20,
    };

    const diagram = {
      id: diagramId,
      name: docSnap.data()?.name || '',
      description: docSnap.data()?.description || '',
      users: docSnap.data()?.users || {},
      access: docSnap.data()?.access || 'public',
      settings: settings,
      ...docSnap.data(),
    } as DiagramModel;

    return {
      nodes,
      edges,
      drilldowns: drilldowns || {},
      diagram,
    };
  } catch (error) {
    console.error('Error loading diagram:', error);
    return {
      nodes: {},
      edges: {},
      drilldowns: {},
      diagram: {
        id: '',
        name: '',
        description: '',
        organizationId: '',
        users: {},
        access: 'public',
        settings: {
          autoSpacing: true,
          nodeDistance: 150,
          snapToGrid: false,
          theme: 'light',
          gridSize: 20,
        },
      },
    };
  }
};
